import { useState, useEffect } from 'react';
import { Title, Select, Modal, Tag, Input } from 'animal-island-ui';
import { useAppStore } from '../store/useAppStore';
import type { LedgerEntry } from '../types';

const EMPTY_ARRAY: LedgerEntry[] = [];

// 倒计时组件（自定义，因 animal-island-ui Time 无 props）
function Countdown({ date, updated }: { date: string; updated: boolean }) {
  let daysLeft = '??';
  let dateStr = '????';
  if (date) {
    const d = new Date(date);
    dateStr = `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
    const diff = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    daysLeft = String(Math.max(0, diff));
  }

  return (
    <div id="topbar-countdown" className="relative flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white border-2 border-amber-200">
      {updated && (
        <div className="absolute -top-2 -right-1 tag-fade-out">
          <Tag color="app-orange" variant="solid" size="small" style={{ background: '#f97316', borderColor: '#f97316' }}>已更新</Tag>
        </div>
      )}
      <span className="text-xs text-gray-400">📅</span>
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-500">{dateStr}</span>
        <span className="text-xs text-gray-300">|</span>
        <span className="text-sm font-bold" style={{ color: '#e63946' }}>{daysLeft}</span>
        <span className="text-xs text-gray-500">天</span>
      </div>
    </div>
  );
}

// 预算组件（自定义，因 Wallet 只展示单值）
function BudgetWallet({ budget, expense, updated }: { budget: number; expense: number; updated: boolean }) {
  const remaining = budget - expense;
  const remainingStr = budget > 0 ? (remaining >= 10000 ? `${(remaining / 10000).toFixed(1)}万` : `${remaining}`) : '??';
  const budgetStr = budget > 0 ? (budget >= 10000 ? `${(budget / 10000).toFixed(1)}万` : `${budget}`) : '??';

  return (
    <div id="topbar-budget" className="relative flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white border-2 border-amber-200">
      {updated && (
        <div className="absolute -top-2 -right-1 tag-fade-out">
          <Tag color="app-orange" variant="solid" size="small" style={{ background: '#f97316', borderColor: '#f97316' }}>已更新</Tag>
        </div>
      )}
      <span className="text-xs">💰</span>
      <span className="text-xs text-gray-400">预算</span>
      <div className="flex items-baseline gap-1">
        <span className="text-sm font-bold" style={{ color: '#e63946' }}>{remainingStr}</span>
        <span className="text-xs text-gray-400">/ {budgetStr}</span>
      </div>
    </div>
  );
}

export default function TopBar() {
  const {
    username, projects, currentProjectId, setCurrentProject,
    updateProjectDate, updateProjectBudget,
    countdownUpdated, budgetUpdated, setCountdownUpdated, setBudgetUpdated,
    loadLedger,
  } = useAppStore();
  const ledgerEntries = useAppStore((s) => {
    if (!currentProjectId) return EMPTY_ARRAY;
    return s.ledger[currentProjectId] || EMPTY_ARRAY;
  });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsAction, setSettingsAction] = useState<'name' | 'budget' | 'date' | null>(null);
  const [tempName, setTempName] = useState(username);
  const [tempBudget, setTempBudget] = useState('');
  const [tempDate, setTempDate] = useState('');

  const project = projects.find((p) => p.id === currentProjectId);

  // 拉取当前项目账本，用于预算消耗展示
  useEffect(() => {
    if (currentProjectId) loadLedger(currentProjectId);
  }, [currentProjectId, loadLedger]);

  if (!project) return null;

  // 计算已支出
  const totalExpense = ledgerEntries.filter((e) => e.type === 'expense').reduce((s, e) => s + e.amount, 0);

  // 项目切换 Select
  const projectOptions = projects.map((p) => ({
    key: String(p.id),
    label: `${username}${p.title}`,
  }));

  const handleSwitchProject = (key: string) => {
    setCurrentProject(Number(key));
  };

  const openSettings = (action: 'name' | 'budget' | 'date') => {
    setSettingsAction(action);
    if (action === 'name') setTempName(username);
    if (action === 'budget') setTempBudget(project.budget > 0 ? String(project.budget) : '');
    if (action === 'date') setTempDate(project.targetDate || '');
    setSettingsOpen(true);
  };

  const handleSettingsOk = () => {
    if (settingsAction === 'name' && tempName.trim()) {
      useAppStore.getState().setUsername(tempName.trim());
    }
    if (settingsAction === 'budget' && tempBudget) {
      updateProjectBudget(project.id, Number(tempBudget));
      setBudgetUpdated(true);
    }
    if (settingsAction === 'date' && tempDate) {
      updateProjectDate(project.id, tempDate);
      setCountdownUpdated(true);
    }
    setSettingsOpen(false);
    setSettingsAction(null);
  };

  const settingsOptions = [
    { key: 'name', label: '更改姓名' },
    { key: 'budget', label: '更改总预算' },
    { key: 'date', label: '更改时间' },
  ];

  return (
    <div id="topbar" className="flex items-center gap-3 px-4 py-3 bg-white border-b-2 border-amber-100">
      {/* 当前喜事项目标题 */}
      <div id="topbar-title" className="flex-shrink-0 flex items-center gap-2">
        <img
          src="https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=Animal%20Crossing%20style%20cute%20wedding%20logo%20with%20double%20happiness%20Chinese%20character%20%E5%9B%8D%2C%20red%20and%20gold%2C%20festive%20celebration%20theme%2C%20horizontal%20banner%20style&image_size=landscape_4_3"
          alt="喜事啊 logo"
          className="w-8 h-8 rounded object-cover"
        />
        <Title size="middle" color="app-red">
          {username}{project.title}
        </Title>
      </div>

      {/* 其他喜事切换 */}
      {projects.length > 1 && (
        <div id="topbar-project-switch" className="flex-shrink-0">
          <Select
            options={projectOptions}
            value={currentProjectId ? String(currentProjectId) : ''}
            onChange={handleSwitchProject}
            placeholder="切换喜事"
          />
        </div>
      )}

      {/* 喜事倒计时 */}
      <Countdown date={project.targetDate} updated={countdownUpdated} />

      {/* 预算消耗 */}
      <BudgetWallet budget={project.budget} expense={totalExpense} updated={budgetUpdated} />

      {/* 设置按钮 */}
      <div id="topbar-settings" className="ml-auto flex-shrink-0">
        <Select
          options={settingsOptions}
          value=""
          onChange={(key) => openSettings(key as 'name' | 'budget' | 'date')}
          placeholder="⚙️ 设置"
        />
      </div>

      {/* 设置 Modal */}
      <Modal
        open={settingsOpen}
        title={settingsAction === 'name' ? '更改姓名' : settingsAction === 'budget' ? '更改总预算' : '更改时间'}
        onClose={() => setSettingsOpen(false)}
        onOk={handleSettingsOk}
        typewriter={false}
      >
        <div id="topbar-settings-modal" className="py-4">
          {settingsAction === 'name' && (
            <Input
              size="middle"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              className="w-full"
              placeholder="输入新姓名"
              autoFocus
            />
          )}
          {settingsAction === 'budget' && (
            <input
              type="number"
              value={tempBudget}
              onChange={(e) => setTempBudget(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-amber-200 focus:border-amber-400 focus:outline-none bg-amber-50"
              placeholder="输入预算金额（元）"
              autoFocus
            />
          )}
          {settingsAction === 'date' && (
            <input
              type="date"
              value={tempDate}
              onChange={(e) => setTempDate(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-amber-200 focus:border-amber-400 focus:outline-none bg-amber-50"
              autoFocus
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
